import React, { useCallback, useEffect, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { ButtonMenuItem, COLLAPSE_ELEM_BEFORE } from '@atlaskit/side-nav-items/button-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { Divider, MenuSection } from '@atlaskit/side-nav-items/menu-section';
import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context';

import type { RecommendedConnector } from '../connector-promo-config-types.ts';
import { useAdminHubConnectorLink } from '../hooks/useAdminHubConnectorLink.ts';

import { ConnectorDismissMenu } from './ConnectorDismissMenu.tsx';
import { ConnectorIconPlaceholder } from './ConnectorIconPlaceholder.tsx';

const styles = cssMap({
	spotlightAnchor: { display: 'inline-flex' },
});

const ConnectorRow = ({
	connector,
	onSetup,
	setupLabel,
	testId,
}: {
	connector: RecommendedConnector;
	onSetup: (key: string) => void;
	setupLabel: string;
	testId: string;
}) => (
	<ButtonMenuItem
		elemBefore={<ConnectorIconPlaceholder connector={connector} size={20} />}
		elemAfter={<Text color="color.text.brand">{setupLabel}</Text>}
		onClick={() => onSetup(connector.key)}
		testId={testId}
	>
		{connector.displayName}
	</ButtonMenuItem>
);

const messages = defineMessages({
	label: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.searchAcrossTools.label',
		defaultMessage: 'Search across tools',
		description: 'Label for the sidebar section showing connector tools',
	},
	setupLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.searchAcrossTools.setupLabel',
		defaultMessage: 'Setup',
		description: 'Button label to set up a connector',
	},
});

export interface SearchAcrossToolsItemProps {
	isSidebarCollapsed?: boolean;
	recommendedConnectors: RecommendedConnector[];
	onSetupConnector: (connectorKey: string) => void;
	onDismiss: () => void;
	/** Whether the spotlight should be shown on the header row. */
	shouldShowSpotlight: boolean;
	/** Called when the spotlight trigger fires; should persist spotlightShown: true. */
	onDismissSpotlight: () => void;
	testId?: string;
}

/** The triggerId registered in Post Office for the Search Across Tools spotlight. */
const SPOTLIGHT_TRIGGER_ID = 'spotlight-search-across-tools';

export const SearchAcrossToolsItem = ({
	isSidebarCollapsed = false,
	recommendedConnectors,
	onSetupConnector,
	onDismiss,
	shouldShowSpotlight,
	onDismissSpotlight,
	testId = SPOTLIGHT_TRIGGER_ID,
}: SearchAcrossToolsItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const adminHubLink = useAdminHubConnectorLink();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const handleSetupClick = useCallback(
		(key: string) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'SearchAcrossToolsItem',
					action: 'clicked',
					actionSubject: 'button',
					attributes: {
						connectorKey: key,
					},
				},
			}).fire();
			onSetupConnector(key);
		},
		[onSetupConnector, createEvent],
	);

	const { fireTrigger } = useFirePostOfficeTrigger({ triggerId: SPOTLIGHT_TRIGGER_ID });

	const triggerActivatedRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// After a 10-second page dwell with the sidebar expanded, activate the
	// Post Office spotlight trigger. The timer only runs while isSidebarCollapsed
	// is false; collapsing the sidebar clears it and expanding starts a fresh one.
	// onDismissSpotlight and triggerActivatedRef are set INSIDE the timeout so
	// that if the user navigates away or collapses before the dwell completes the
	// spotlight can retry on the next opportunity.
	useEffect(() => {
		if (!shouldShowSpotlight || isSidebarCollapsed || triggerActivatedRef.current) {
			return;
		}

		timerRef.current = setTimeout(() => {
			triggerActivatedRef.current = true;
			fireTrigger({ isActive: true });
			onDismissSpotlight();
		}, 10_000);

		return () => {
			if (timerRef.current !== null) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [shouldShowSpotlight, isSidebarCollapsed, fireTrigger, onDismissSpotlight]);

	// Deactivate the trigger when the component unmounts.
	useEffect(() => {
		return () => {
			fireTrigger({ isActive: false });
		};
	}, [fireTrigger]);

	return (
		<MenuSection>
			<Divider />
			<LinkMenuItem
				href={adminHubLink ?? '/admin'}
				target="_blank"
				elemAfter={<LinkExternalIcon label="" color="currentColor" size="small" />}
				elemBefore={COLLAPSE_ELEM_BEFORE}
				actions={
					<Flex alignItems="center">
						<ConnectorDismissMenu onDismiss={onDismiss} testId={`${testId}--dismiss-menu`} />
						<div data-testId={`${SPOTLIGHT_TRIGGER_ID}--header`} css={styles.spotlightAnchor} />
					</Flex>
				}
				testId={`${testId}--header-item`}
			>
				{formatMessage(messages.label)}
			</LinkMenuItem>
			{recommendedConnectors.slice(0, 3).map((connector) => (
				<ConnectorRow
					key={connector.key}
					connector={connector}
					onSetup={handleSetupClick}
					setupLabel={formatMessage(messages.setupLabel)}
					testId={`${testId}--connector-${connector.key}`}
				/>
			))}
		</MenuSection>
	);
};
